import {
  NotifyData,
  PlainNotificationItem,
  StandardNotificationItem,
  WsNotification,
} from "@/types/notification";
import { Notification } from "@/types/entity";
import { useMateCommit } from "./mateCommit";
import { useAdmire } from "./admire";
import { plainNotificationItemFactory } from "@/modules/notification-helper";
import { NotificationTypeEnum } from "@/enums/notificationEnum";
import { useGlobalModal } from "./globalModal";
import { showConfirmDialog, showDialog, showLoadingToast } from "vant";
import { readMateSprite } from "@/apis/script-game";
import { useMate } from "./mate";
import { useDungeon } from "./dungeon";
import i18n from "@/locales";
import { MemberType } from "@/enums/mateEnums";
import { getMemberTypeByPlanId } from "@/views/Crystal/memberData";

function supplyOnClick(po: PlainNotificationItem): StandardNotificationItem {
  return {
    onClick: (router) => {
      po.path && router.push(po.path);
    },
    ...po,
  };
}

function fromImageTask(n: PlainNotificationItem): StandardNotificationItem {
  return {
    ...n,
    onClick: (router) => {
      if (router.currentRoute.value.name === "beautify") {
        return;
      } else {
        router.push({
          name: "beautify",
          query: {
            mode: "history",
          },
        });
      }
    },
  };
}

function fromFollowOrAdmire(
  n: PlainNotificationItem
): StandardNotificationItem {
  return {
    ...n,
    onClick: (router) => {
      const param = {
        name: "friendList",
        hash: n.type === 10 ? "#follower" : "#admirer",
      };
      if (router.currentRoute.value.name === param.name) {
        router.replace({ hash: param.hash });
      } else {
        router.push(param);
      }
    },
  };
}

function fromStoryVideo(n: PlainNotificationItem): StandardNotificationItem {
  return {
    ...n,
    onClick: (router) => {
      if (router.currentRoute.value.name === "imageGenerateList") {
        router.replace({ hash: "#toMark" });
      } else {
        router.push({
          name: "imageGenerateList",
          hash: "#toMark",
        });
      }
    },
  };
}

function fromAdmire(n: PlainNotificationItem): StandardNotificationItem {
  if (n.time === undefined) useMateCommit().getPendingList();
  return {
    ...n,
    onClick: (router) => {
      router.push(`/request/list`);
    },
  };
}

function fromAdmireResponse(
  n: PlainNotificationItem
): StandardNotificationItem {
  if (n.time === undefined) {
    const admire = useAdmire();
    admire.reloadIdx++;
    admire.fetchAdmiringList();
  }
  return {
    ...n,
    onClick: (router) => {
      router.push({ name: "friendList" });
    },
  };
}

function fromNewSiteMessage(
  n: PlainNotificationItem
): StandardNotificationItem {
  return {
    ...n,
    onClick: () => {
      useGlobalModal().openNewSiteMessageModal({
        uuid: n.path,
        title: n.title!,
      });
    },
  };
}

function fromUserShareReward(
  n: PlainNotificationItem
): StandardNotificationItem {
  return {
    ...n,
    onClick: () => {
      showDialog({
        title: n.title!,
        message: `${n.content!}。${i18n.global.t(
          "Message.contactCustomerServiceDesc2"
        )}`,
      });
    },
  };
}

function fromSpriteGenerated(
  n: PlainNotificationItem
): StandardNotificationItem {
  return {
    ...n,
    onClick: async (router) => {
      const { spriteId, spriteUuid } = n.originData!;
      const loading = showLoadingToast(i18n.global.t("Message.loading"));
      try {
        const { item } = await readMateSprite(spriteUuid);
        await useDungeon().fetchMateSpriteList(item.mateId!, true);
        await useMate().changeCurrentMate(item.mateId!);
        router.push(`/mate/create?spriteId=${spriteId}`);
        loading.close();
      } catch {
        loading.close();
      }
    },
  };
}

function fromMemberSubscriptionReceived(
  n: PlainNotificationItem
): StandardNotificationItem {
  return {
    ...n,
    onClick: (router) => {
      console.log(n, router);
      const memberType =
        getMemberTypeByPlanId(n.originData?.subscription.planId) || 0;
      let powerAmount = 0;
      let generateTimes = 0;

      if (memberType === MemberType.Standard) {
        powerAmount = 200;
        generateTimes = 1;
      } else if (memberType === MemberType.Premium) {
        powerAmount = 600;
        generateTimes = 5;
      } else if (memberType === MemberType.Basic) {
        powerAmount = 30;
        generateTimes = 0;
      }
      const name = i18n.global.t(`User.memberType.${memberType}`);
      console.log(name);
      showConfirmDialog({
        message:
          i18n.global.t("Notifications.type.memberSubscriptionReceivedDesc", {
            name,
            powerAmount,
          }) +
          (generateTimes
            ? i18n.global.t(
                "Notifications.type.memberSubscriptionReceivedGenerateTimes",
                {
                  generateTimes,
                }
              )
            : ""),
        confirmButtonText: i18n.global.t("Buttons.ok"),
        showCancelButton: false,
      });
    },
  };
}

export function standardNotificationItemFactory(
  n: Notification<NotifyData> | WsNotification<NotifyData>
): StandardNotificationItem {
  const po = plainNotificationItemFactory(n);
  switch (n.type) {
    case 7:
      return fromImageTask(po);
    case 10:
    case 11:
      return fromFollowOrAdmire(po);
    case 15:
      return fromStoryVideo(po);
    case 17:
      return fromAdmire(po);
    case 18:
      return fromAdmireResponse(po);
    case 35:
      return fromNewSiteMessage(po);
    case 36:
      return fromUserShareReward(po);
    case NotificationTypeEnum.SpriteGenerated:
      return fromSpriteGenerated(po);
    case NotificationTypeEnum.MemberSubscriptionReceived:
      return fromMemberSubscriptionReceived(po);
    case NotificationTypeEnum.ChatMessageEarned:
    case 4:
    case 5:
    case 14:
    default:
      return supplyOnClick(po);
  }
}

export default {
  standardNotificationItemFactory,
};
